import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import type { Descendant } from 'slate'
import { createEditor, Editor, Range } from 'slate'
import type { RenderElementProps, RenderLeafProps } from 'slate-react'
import { withReact } from 'slate-react'
import type { Suggestion } from 'shared/components/suggest-mention/types'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import { useSuggestionsList } from 'modules/community/api/use-suggestions-list'
import { insertText, serializeHTML } from 'modules/community/hooks/slate-editor-utils'
import type { CustomEditor } from 'modules/community/types/custom-editor'
import { withImages } from '../plugins/with-images'
import { withLinks } from '../plugins/with-links'
import { withMentions } from '../plugins/with-mentions'
import { Element } from '../utils/element'
import { Leaf } from '../utils/leaf'

interface UseSlateEditorProps {
  initialContent: Descendant[]
}

export const useSlateEditor = ({
  initialContent,
}: UseSlateEditorProps): {
  editor: CustomEditor
  content: Descendant[]
  setContent: React.Dispatch<React.SetStateAction<Descendant[]>>
  renderElement: (props: RenderElementProps) => React.JSX.Element
  renderLeaf: (props: RenderLeafProps) => React.JSX.Element
  updateEditorContent: (value: Descendant[]) => void
  target: Range | null
  setTarget: React.Dispatch<React.SetStateAction<Range | null>>
  suggestions: Suggestion[] | undefined
  suggestionsLoading: boolean
  serializedTextHtml: string
  insertEmoji: (emoji: string) => void
  contentError: string
  setContentError: React.Dispatch<React.SetStateAction<string>>
} => {
  const { query } = useRouter()
  const { communityPath } = query

  const [content, setContent] = useState<Descendant[]>(initialContent)
  const [serializedTextHtml, setSerializedTextHtml] = useState('')
  const [target, setTarget] = useState<Range | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [contentError, setContentError] = useState('')

  const editor = useMemo(() => withImages(withLinks(withReact(withMentions(createEditor())))), [])
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, [])
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, [])

  const { data: suggestions, isValidating: suggestionsLoading } = useSuggestionsList({
    communityPath: String(communityPath || ''),
    displayNameSearchBy: debouncedSearch,
  })

  const updateEditorContent = (value: Descendant[]): void => {
    setContentError('')
    setContent(value)
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection)
      const wordBefore = Editor.before(editor, start, { unit: 'word' })
      const before = wordBefore && Editor.before(editor, wordBefore)
      const beforeRange = before && Editor.range(editor, before, start)
      const beforeText = beforeRange && Editor.string(editor, beforeRange)
      const beforeMatch = beforeText && beforeText.match(/(?:^|\s)@([^\s@]*)(\s|$)/)
      const after = Editor.after(editor, start)
      const afterRange = Editor.range(editor, start, after)
      const afterText = Editor.string(editor, afterRange)
      const afterMatch = afterText.match(/^(\s|$)/)

      const twoCharsBefore =
        wordBefore &&
        Editor.before(
          editor,
          { ...wordBefore, offset: wordBefore.offset },
          { unit: 'character', distance: 2 },
        )
      const twoCharsRange = twoCharsBefore && Editor.range(editor, twoCharsBefore, start)
      const twoCharsText = twoCharsRange && Editor.string(editor, twoCharsRange)

      const isEmptyBeforeAtRegular = new RegExp(/(?:^|\s)@/)
      const isEmptyBeforeAt = isEmptyBeforeAtRegular.test(twoCharsText || '')

      if (beforeMatch?.[1] && afterMatch && isEmptyBeforeAt) {
        setTarget(beforeRange || twoCharsRange || null)
        setSearch(beforeMatch[1])
        return
      } else {
        const pointBefore = Editor.before(editor, start, { distance: 2 })
        const pointRange = pointBefore && Editor.range(editor, pointBefore, start)
        const characterBefore = pointRange && Editor.string(editor, pointRange)

        const pointNormal = Editor.before(editor, start)
        const pointNormalRange = pointNormal && Editor.range(editor, pointNormal, start)

        if (characterBefore === ' @' || characterBefore === '@') {
          setTarget(pointNormalRange || pointRange || beforeRange || null)
          setSearch('')
          return
        }
      }
    }

    setTarget(null)
  }

  const insertEmoji = (emoji: string): void => {
    insertText(editor, emoji)
  }

  useEffect(() => {
    const textHtml = serializeHTML(editor)
    setSerializedTextHtml(textHtml || '')
  }, [content])

  return {
    editor,
    content,
    setContent,
    renderElement,
    renderLeaf,
    updateEditorContent,
    target,
    setTarget,
    suggestions,
    suggestionsLoading,
    serializedTextHtml,
    insertEmoji,
    contentError,
    setContentError,
  }
}
